/**
 * Resolve {{variable}} paths to editable field metadata (type, ref dropdown, owner).
 * Template {{salesLocation | name}} → edit salesLocation (id), not the string "name".
 * Template {{inventoryUnit | manufacturer | name}} → edit manufacturer (id) on picked unit.
 */

import {
  fieldAppliesToModel,
  getAdditionalFieldsColumns,
  getEntityRecordLabel
} from '../../helpers/formHelpers';
import { camelCaseToTitle } from '../../helpers/docHubFormHelpers';
import { formJson as crmFormJson } from '../crm/form';
import {
  additionalFieldsRow as contractAdditionalFields,
  mainFieldsRows as contractMainFieldRows
} from '../main/form';
import { formJson as inventoryFormJson } from '../inventory/form';
import { INVENTORY_DELETION_ROWS } from '../inventory/inventoryWorksheetHelpers';
import { getCustomEntityPathParts, isInventoryPath } from './documentStateHelpers';
import { getMilestoneConfigFromPath } from './milestoneDateHelpers';

/** Object refs backed by appChoices (same keys as V1 addChoicesTo*Form). */
const DOCUMENT_REF_FIELDS = {
  salesLocation: { appChoicesKey: 'salesLocation', labelKey: 'location' },
  lender: { appChoicesKey: 'lender', labelKey: 'lienholderCorporateName' },
  insuranceCompany: { appChoicesKey: 'insuranceCompanies', labelKey: 'lienholderCorporateName' },
  installer: { appChoicesKey: 'installer', labelKey: 'name' },
  signature: { appChoicesKey: 'members', labelKey: 'name' },
  leadSource: { appChoicesKey: 'Lead Source', labelKey: 'name', valueIsString: true },
  typeOfUnit: { appChoicesKey: 'Type Of Unit', labelKey: 'name', valueIsString: true },
  contactStatus: { appChoicesKey: 'Contact Status', labelKey: 'name', valueIsString: true },
  loanType: { appChoicesKey: 'Loan Type', labelKey: 'name', valueIsString: true },
  utilities: { appChoicesKey: 'Utilities', labelKey: 'name', valueIsString: true },
  billbackStatus: { appChoicesKey: 'Bill-Back Status', labelKey: 'name', valueIsString: true },
  walkthroughStatus: { appChoicesKey: 'Walkthrough Status', labelKey: 'name', valueIsString: true },
  techStatus: { appChoicesKey: 'Tech Status', labelKey: 'name', valueIsString: true },
  fundingStatus: { appChoicesKey: 'Funding Status', labelKey: 'name', valueIsString: true },
  acStatus: { appChoicesKey: 'AC Status', labelKey: 'name', valueIsString: true }
};

const INVENTORY_REF_FIELDS = {
  manufacturer: { appChoicesKey: 'manufacturer', labelKey: 'factoryShortName' },
  unitLocation: { appChoicesKey: 'salesLocation', labelKey: 'location' },
  lender: { appChoicesKey: 'lender', labelKey: 'lienholderCorporateName' },
  floorplanLender: { appChoicesKey: 'floorplanLender', labelKey: 'name' },
  type: { appChoicesKey: 'Type', labelKey: 'name', valueIsString: true },
  exteriorSiding: { appChoicesKey: 'Exterior Siding', labelKey: 'name', valueIsString: true },
  typeOfUnit: { appChoicesKey: 'Type Of Unit', labelKey: 'name', valueIsString: true },
  model: { appChoicesKey: 'model', labelKey: 'modelName', dependsOnManufacturer: true }
};

const CONTRACT_STATUS_FIELDS = {
  status: { appChoicesKey: 'Status', labelKey: 'name', valueIsString: true }
};

export const normalizeRefId = value => {
  if (value === undefined || value === null || value === '') return '';
  if (typeof value === 'object' && value._id != null) return String(value._id);
  return String(value);
};

const lookupRefConfig = (fieldKey, owner, docSource) => {
  if (owner === 'inventory') {
    return INVENTORY_REF_FIELDS[fieldKey] || null;
  }
  if (fieldKey === 'status') {
    return docSource === 'Contract' ? CONTRACT_STATUS_FIELDS.status : DOCUMENT_REF_FIELDS.contactStatus;
  }
  if (fieldKey === 'contactStatus') {
    return DOCUMENT_REF_FIELDS.contactStatus;
  }
  return DOCUMENT_REF_FIELDS[fieldKey] || null;
};

const searchFields = () => [
  ...contractMainFieldRows().flatMap(r => r.columns),
  ...contractAdditionalFields.columns
];

let crmSearchFieldsCache = null;
const getCrmSearchFields = () => {
  if (crmSearchFieldsCache) return crmSearchFieldsCache;
  crmSearchFieldsCache = (crmFormJson().forms?.[0]?.rows || []).flatMap(row => row.columns || []);
  return crmSearchFieldsCache;
};

let inventorySearchFieldsCache = null;
const getInventorySearchFields = () => {
  if (inventorySearchFieldsCache) return inventorySearchFieldsCache;
  const noop = () => {};
  inventorySearchFieldsCache = inventoryFormJson({
    numberOfSections: 1,
    onNumberOfSectionsChange: noop,
    onSizeOverallChange: noop,
    onManufacturerChange: noop,
    onModelChange: noop,
    onCreateModelClick: noop,
    onUnitLocationChange: noop
  }).forms[0].rows.flatMap(row => row.columns || []);
  return inventorySearchFieldsCache;
};

const lookupFormFieldMeta = (fieldKey, owner) => {
  const columns =
    owner === 'inventory'
      ? getInventorySearchFields()
      : [...searchFields(), ...getCrmSearchFields()];
  return columns.find(
    column => column.field?.key === fieldKey || column.field?.id === fieldKey
  )?.field;
};

const getEntityDefinitions = appChoices =>
  (appChoices || []).find(a => a.key === 'entityDefinitions')?.values || [];

export const getEntityDefinitionByName = (appChoices, name, docSource) => {
  if (!name) return null;
  return (
    getEntityDefinitions(appChoices).find(
      definition => definition.name === name && (!docSource || fieldAppliesToModel(definition, docSource))
    ) || null
  );
};

export const getCustomEntityRecord = (appChoices, definitionId, recordId) => {
  if (!definitionId || recordId === undefined || recordId === null || recordId === '') return null;
  const definition = getEntityDefinitions(appChoices).find(d => String(d._id) === String(definitionId));
  if (!definition) return null;
  return (definition.records || []).find(record => String(record._id) === String(recordId)) || null;
};

const getAdditionalFieldLabel = (key, appChoices, docSource) => {
  const meta = lookupAdditionalFieldMeta(key, appChoices, docSource);
  return meta?.title || meta?.label || camelCaseToTitle(key);
};

/** DocHub admin formFields (appChoices) — same source V1 uses for additional fields. */
const lookupAdditionalFieldMeta = (key, appChoices, docSource) => {
  if (!key) return null;
  const columns = getAdditionalFieldsColumns({
    appChoices,
    model: docSource,
    keysToUse: [key]
  });
  return columns[0]?.field || null;
};

let nestedInventoryFieldLabelsCache = null;
const getNestedInventoryFieldLabels = () => {
  if (nestedInventoryFieldLabelsCache) return nestedInventoryFieldLabelsCache;
  nestedInventoryFieldLabelsCache = Object.fromEntries(
    INVENTORY_DELETION_ROWS.map(([label, key]) => [key, label])
  );
  return nestedInventoryFieldLabelsCache;
};

export const getVariableFieldHint = ({
  owner,
  fieldKey,
  displayLeaf,
  statePath,
  isRefField,
  appChoices,
  docSource,
  pickedInventory
}) => {
  if (owner === 'inventory' && !pickedInventory) {
    return 'Select inventory first';
  }

  if (fieldKey === 'additionalFields' && displayLeaf) {
    return getAdditionalFieldLabel(displayLeaf, appChoices, docSource);
  }

  if ((fieldKey === 'custom' || fieldKey === 'customEntities') && displayLeaf) {
    return camelCaseToTitle(displayLeaf);
  }

  // Ref fields edit the object key (e.g. manufacturer), not the display leaf (e.g. name).
  if (!isRefField && displayLeaf) {
    const dotPath = statePath?.length > 1 ? statePath.join('.') : `${fieldKey}.${displayLeaf}`;
    const worksheetLabel = getNestedInventoryFieldLabels()[dotPath];
    if (worksheetLabel) return worksheetLabel;

    const nestedMeta = lookupFormFieldMeta(displayLeaf, owner);
    if (nestedMeta?.title) return nestedMeta.title;
    if (nestedMeta?.hint) return nestedMeta.hint;

    return camelCaseToTitle(displayLeaf);
  }

  const fieldMeta = lookupFormFieldMeta(fieldKey, owner);
  if (fieldMeta?.title) return fieldMeta.title;
  if (fieldMeta?.hint) return fieldMeta.hint;

  return camelCaseToTitle(fieldKey || displayLeaf || '');
};

const getChoiceValues = (appChoices, appChoicesKey) => {
  const choice = (appChoices || []).find(a => a.key === appChoicesKey);
  return choice?.values || [];
};

const getRefOptionLabel = (item, labelKey, displayLeaf) => {
  if (displayLeaf) {
    const leafValue = item?.[displayLeaf];
    return leafValue === undefined || leafValue === null || leafValue === '' ? 'N/A' : String(leafValue);
  }

  const label =
    item?.[labelKey] ||
    item?.name ||
    item?.location ||
    item?.factoryShortName ||
    item?.lienholderCorporateName ||
    item?.modelName ||
    item?._id ||
    '';
  return label === '' ? 'N/A' : String(label);
};

const buildRefOptions = (values, labelKey, valueIsString, displayLeaf) => {
  if (!values.length) return { options: [], optionValues: [] };

  if (valueIsString || typeof values[0] !== 'object') {
    const strings = values.map(v => String(v));
    return { options: strings, optionValues: strings };
  }

  const options = values.map(item => getRefOptionLabel(item, labelKey, displayLeaf));
  const optionValues = values.map(item => String(item._id));
  return { options, optionValues };
};

const getModelChoicesForManufacturer = (appChoices, manufacturerId, populatedModel) => {
  const allModels = getChoiceValues(appChoices, 'model').filter(Boolean);
  const filtered = manufacturerId
    ? allModels.filter(
        m => normalizeRefId(typeof m.manufacturer === 'object' ? m.manufacturer : m.manufacturer) === manufacturerId
      )
    : [];

  if (filtered.length) return filtered;
  if (populatedModel && typeof populatedModel === 'object') return [populatedModel];
  return [];
};

export const resolveVariableFieldBinding = ({
  path,
  appChoices = [],
  docSource,
  pickedInventory
}) => {
  if (!path?.length) {
    return { owner: 'document', fieldKey: '', statePath: [], type: 'text' };
  }

  const inventoryBound = isInventoryPath(path);
  const owner = inventoryBound ? 'inventory' : 'document';
  const fieldKey = inventoryBound ? path[1] : path[0];
  const displayLeaf = path.length > (inventoryBound ? 2 : 1) ? path[path.length - 1] : null;

  const milestoneConfig = !inventoryBound ? getMilestoneConfigFromPath(path) : null;
  if (milestoneConfig) {
    return {
      owner: 'document',
      fieldKey: milestoneConfig.key,
      displayLeaf,
      statePath: [milestoneConfig.key],
      type: 'milestone',
      milestoneConfig,
      hint: `${milestoneConfig.name} date`
    };
  }

  const customPath = getCustomEntityPathParts(path);
  if (customPath?.entityName) {
    const { entityName, inventoryBound: customOnInventory } = customPath;
    const columnKey = customPath.displayLeaf || displayLeaf;
    const definition = getEntityDefinitionByName(
      appChoices,
      entityName,
      customOnInventory ? 'Inventory' : docSource
    );
    const definitionId = definition?._id ? String(definition._id) : '';
    const records = definition?.records || [];
    const options = records.map(record =>
      columnKey
        ? getRefOptionLabel(record, null, columnKey)
        : getEntityRecordLabel(record, definition?.columns)
    );
    const optionValues = records.map(record => String(record._id));
    const column = (definition?.columns || []).find(col => col.key === columnKey);
    const hint = column?.label ? `${entityName} - ${column.label}` : entityName;

    return {
      owner: customOnInventory ? 'inventory' : 'document',
      fieldKey: 'customEntities',
      displayLeaf: columnKey,
      statePath: definitionId ? ['customEntities', definitionId] : ['customEntities', entityName],
      type: 'ref',
      options,
      optionValues,
      hint
    };
  }

  const refConfig = lookupRefConfig(fieldKey, owner, docSource);
  // Ref fields store the object id at fieldKey — display leaves (| name, | location) are template-only.
  const statePath = refConfig
    ? [fieldKey]
    : inventoryBound
      ? path.slice(1)
      : path;

  const hint = getVariableFieldHint({
    owner,
    fieldKey,
    displayLeaf,
    statePath,
    isRefField: Boolean(refConfig),
    appChoices,
    docSource,
    pickedInventory
  });

  if (refConfig) {
    if (refConfig.dependsOnManufacturer) {
      const manufacturerId = normalizeRefId(pickedInventory?.manufacturer);
      const populatedModel = pickedInventory?.model;
      const modelChoices = getModelChoicesForManufacturer(
        appChoices,
        manufacturerId,
        typeof populatedModel === 'object' ? populatedModel : undefined
      );
      const { options, optionValues } = buildRefOptions(
        modelChoices,
        refConfig.labelKey || 'modelName',
        false,
        displayLeaf
      );
      return {
        owner,
        fieldKey,
        displayLeaf,
        statePath,
        type: 'ref',
        refConfig,
        options,
        optionValues,
        hint
      };
    }

    const values = getChoiceValues(appChoices, refConfig.appChoicesKey);
    const { options, optionValues } = buildRefOptions(
      values,
      refConfig.labelKey || 'name',
      refConfig.valueIsString,
      displayLeaf
    );

    return {
      owner,
      fieldKey,
      displayLeaf,
      statePath,
      type: 'ref',
      refConfig,
      options,
      optionValues,
      hint
    };
  }

  const leaf = path[path.length - 1];
  const additionalMeta =
    fieldKey === 'additionalFields' && displayLeaf
      ? lookupAdditionalFieldMeta(displayLeaf, appChoices, docSource)
      : null;
  const fieldMeta =
    additionalMeta || lookupFormFieldMeta(fieldKey, owner) || lookupFormFieldMeta(leaf, owner);

  let type = 'text';
  let inputType = 'text';
  if (fieldMeta?.type === 'number') {
    type = 'number';
    inputType = 'number';
  } else if (fieldMeta?.variant === 'date' || fieldMeta?.type === 'date') {
    inputType = 'date';
  } else if (
    fieldMeta?.type === 'dropdown' &&
    Array.isArray(fieldMeta.options) &&
    fieldMeta.options.length
  ) {
    // Treat DocHub additional dropdowns as string refs (option label = value).
    return {
      owner,
      fieldKey,
      displayLeaf,
      statePath,
      type: 'ref',
      options: fieldMeta.options.map(String),
      optionValues: (fieldMeta.optionValues || fieldMeta.options).map(String),
      hint
    };
  }

  return {
    owner,
    fieldKey,
    displayLeaf,
    statePath,
    type,
    inputType,
    hint
  };
};
